const databaseName = "portability-offline-v1";
const keyStore = "keys";
const tripStore = "trips";
const mutationStore = "mutations";

export interface OfflineTripSummary {
  readonly tripId: string;
  readonly savedAt: string;
  readonly syncState: "synced" | "pending" | "conflict";
}

export interface OfflineTripMutation {
  readonly mutationId: string;
  readonly type: "manual_item_added" | "trip_note_updated";
  readonly payload: unknown;
  readonly createdAt?: string;
}

export interface OfflineTripEssentialsPayload {
  readonly serverFiltered: true;
  readonly generatedAt: string;
  readonly trip: {
    readonly tripId: string;
    readonly name: string;
    readonly destination: string;
    readonly startsOn: string;
    readonly endsOn: string;
  };
  readonly essentials: {
    readonly tripId: string;
    readonly encryptedAtRestRequired: true;
    readonly syncOnReconnect: true;
    readonly liveInventoryActionsRequireInternet: true;
    readonly savedItemIds: readonly string[];
  };
  readonly items: readonly {
    readonly itemId: string;
    readonly source: string;
    readonly title: string;
    readonly startsAt?: string;
    readonly endsAt?: string;
    readonly localDate?: string;
    readonly location?: string;
  }[];
  readonly onlineOnlyActions: readonly string[];
  readonly sensitiveFieldsExcluded: readonly string[];
}

export interface OfflineTripSyncInput {
  readonly tripId: string;
  readonly cachedPayload: unknown;
  readonly mutations: readonly OfflineTripMutation[];
}

export interface OfflineTripSyncResult {
  readonly tripId: string;
  readonly status: "synced" | "conflict" | "skipped";
  readonly serverPayload?: unknown;
}

function assertOfflineTripEssentialsPayload(payload: unknown): asserts payload is OfflineTripEssentialsPayload {
  if (typeof payload !== "object" || payload === null || Array.isArray(payload)) throw new Error("Offline payload must be a server-filtered object");
  const value = payload as Partial<OfflineTripEssentialsPayload>;
  if (value.serverFiltered !== true) throw new Error("Offline payload must be server-filtered");
  if (typeof value.trip !== "object" || value.trip === null || Array.isArray(value.trip)) throw new Error("Offline payload trip is required");
  if (typeof value.essentials !== "object" || value.essentials === null || Array.isArray(value.essentials)) throw new Error("Offline payload essentials are required");
  if (!Array.isArray(value.items) || !Array.isArray(value.onlineOnlyActions) || !Array.isArray(value.sensitiveFieldsExcluded)) throw new Error("Offline payload lists are malformed");
}

async function encryptOfflineRecord(database: IDBDatabase, payload: OfflineTripEssentialsPayload, syncState: OfflineTripSummary["syncState"]) {
  const key = await deviceKey(database);
  const initializationVector = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(payload));
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv: initializationVector }, key, plaintext);
  return {
    version: 1,
    algorithm: "AES-GCM",
    initializationVector,
    ciphertext,
    savedAt: new Date().toISOString(),
    syncState
  };
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(databaseName, 2);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(keyStore)) database.createObjectStore(keyStore);
      if (!database.objectStoreNames.contains(tripStore)) database.createObjectStore(tripStore);
      if (!database.objectStoreNames.contains(mutationStore)) database.createObjectStore(mutationStore);
    };
    request.onerror = () => reject(request.error ?? new Error("Offline database could not be opened"));
    request.onsuccess = () => resolve(request.result);
  });
}

function transactionComplete(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("Offline database transaction failed"));
    transaction.onabort = () => reject(transaction.error ?? new Error("Offline database transaction aborted"));
  });
}

function transactionResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onerror = () => reject(request.error ?? new Error("Offline database operation failed"));
    request.onsuccess = () => resolve(request.result);
  });
}

async function deviceKey(database: IDBDatabase): Promise<CryptoKey> {
  const current = await transactionResult(database.transaction(keyStore).objectStore(keyStore).get("device")) as CryptoKey | undefined;
  if (current !== undefined) return current;
  const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
  await transactionResult(database.transaction(keyStore, "readwrite").objectStore(keyStore).put(key, "device"));
  return key;
}

export async function saveEncryptedOfflineTrip(tripId: string, payload: OfflineTripEssentialsPayload): Promise<void> {
  if (!tripId.trim()) throw new Error("Offline trip id is required");
  assertOfflineTripEssentialsPayload(payload);
  if (payload.trip.tripId !== tripId || payload.essentials.tripId !== tripId) throw new Error("Offline payload must belong to the saved trip");
  const database = await openDatabase();
  try {
    await transactionResult(database.transaction(tripStore, "readwrite").objectStore(tripStore).put(await encryptOfflineRecord(database, payload, "synced"), tripId));
  } finally {
    database.close();
  }
}

export async function listEncryptedOfflineTrips(): Promise<readonly OfflineTripSummary[]> {
  const database = await openDatabase();
  try {
    const store = database.transaction(tripStore).objectStore(tripStore);
    const keys = await transactionResult(store.getAllKeys());
    const summaries = await Promise.all(keys.map(async (key) => {
      const value = await transactionResult(store.get(key)) as { readonly savedAt?: string; readonly syncState?: OfflineTripSummary["syncState"] } | undefined;
      return {
        tripId: String(key),
        savedAt: value?.savedAt ?? "",
        syncState: value?.syncState ?? "synced"
      };
    }));
    return summaries.sort((left, right) => right.savedAt.localeCompare(left.savedAt));
  } finally {
    database.close();
  }
}

export async function loadEncryptedOfflineTrip<T = unknown>(tripId: string): Promise<T | null> {
  if (!tripId.trim()) throw new Error("Offline trip id is required");
  const database = await openDatabase();
  try {
    const record = await transactionResult(database.transaction(tripStore).objectStore(tripStore).get(tripId)) as { readonly initializationVector: BufferSource; readonly ciphertext: BufferSource } | undefined;
    if (record === undefined) return null;
    const key = await deviceKey(database);
    const plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv: record.initializationVector }, key, record.ciphertext);
    return JSON.parse(new TextDecoder().decode(plaintext)) as T;
  } finally {
    database.close();
  }
}

export async function deleteEncryptedOfflineTrip(tripId: string): Promise<void> {
  if (!tripId.trim()) throw new Error("Offline trip id is required");
  const database = await openDatabase();
  try {
    const transaction = database.transaction([tripStore, mutationStore], "readwrite");
    transaction.objectStore(tripStore).delete(tripId);
    transaction.objectStore(mutationStore).delete(tripId);
    await transactionComplete(transaction);
  } finally {
    database.close();
  }
}

export async function queueOfflineTripMutation(tripId: string, mutation: OfflineTripMutation): Promise<void> {
  if (!tripId.trim()) throw new Error("Offline trip id is required");
  if (!mutation.mutationId.trim()) throw new Error("Offline mutation id is required");
  const database = await openDatabase();
  try {
    const transaction = database.transaction([tripStore, mutationStore], "readwrite");
    const trip = await transactionResult(transaction.objectStore(tripStore).get(tripId)) as Record<string, unknown> | undefined;
    if (trip === undefined) throw new Error("Offline trip must be saved before mutations can queue");
    const existing = await transactionResult(transaction.objectStore(mutationStore).get(tripId)) as OfflineTripMutation[] | undefined;
    transaction.objectStore(mutationStore).put([...existing ?? [], { ...mutation, createdAt: mutation.createdAt ?? new Date().toISOString() }], tripId);
    transaction.objectStore(tripStore).put({ ...trip, syncState: "pending" }, tripId);
    await transactionComplete(transaction);
  } finally {
    database.close();
  }
}

export async function syncEncryptedOfflineTrips(syncer: (input: OfflineTripSyncInput) => Promise<OfflineTripSyncResult>): Promise<readonly OfflineTripSyncResult[]> {
  const database = await openDatabase();
  try {
    const tripStoreHandle = database.transaction(tripStore).objectStore(tripStore);
    const tripIds = (await transactionResult(tripStoreHandle.getAllKeys())).map(String);
    const results: OfflineTripSyncResult[] = [];
    for (const tripId of tripIds) {
      const mutations = await transactionResult(database.transaction(mutationStore).objectStore(mutationStore).get(tripId)) as OfflineTripMutation[] | undefined;
      if ((mutations?.length ?? 0) === 0) continue;
      const cachedPayload = await loadEncryptedOfflineTrip(tripId);
      if (cachedPayload === null) continue;
      let result: OfflineTripSyncResult;
      try {
        result = await syncer({ tripId, cachedPayload, mutations: mutations ?? [] });
      } catch {
        result = { tripId, status: "skipped" };
      }
      const encryptedServerPayload = result.status === "synced"
        ? await (() => {
          const nextPayload = result.serverPayload ?? cachedPayload;
          assertOfflineTripEssentialsPayload(nextPayload);
          return encryptOfflineRecord(database, nextPayload, "synced");
        })()
        : null;
      const transaction = database.transaction([tripStore, mutationStore], "readwrite");
      const current = await transactionResult(transaction.objectStore(tripStore).get(tripId)) as Record<string, unknown> | undefined;
      if (current !== undefined && result.status === "synced") {
        transaction.objectStore(mutationStore).delete(tripId);
        transaction.objectStore(tripStore).put(encryptedServerPayload!, tripId);
      } else if (current !== undefined && result.status === "conflict") {
        transaction.objectStore(tripStore).put({ ...current, syncState: "conflict" }, tripId);
      } else if (current !== undefined && result.status === "skipped") {
        transaction.objectStore(tripStore).put({ ...current, syncState: "pending" }, tripId);
      }
      await transactionComplete(transaction);
      results.push(result);
    }
    return results;
  } finally {
    database.close();
  }
}
